"use client";

import React, { useState } from "react";

export default function ConsultationForm(props) {
  const title = props?.title || "Book Free Consultation";
  const buttonText = props?.buttonText || "Submit Your Requirement!";

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    companyName: "",
    message: ""
  });

  const [focused, setFocused] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
      const res = await fetch(`${apiUrl}/api/leads/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      let data = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await res.json();
      }

      if (res.ok) {
        setSubmitted(true);
        setStatus({ type: "success", message: "Thanks! We'll get back to you within 24 hours." });
        setForm({ fullName: "", email: "", phone: "", companyName: "", message: "" });
      } else {
        setStatus({ type: "error", message: data.message || "Something went wrong. Please try again." });
      }
    } catch (err) {
      setStatus({ type: "error", message: "Network error. Please check your connection." });
    } finally {
      setLoading(false);
    }
  };

  const handleFocus = (name) => setFocused((f) => ({ ...f, [name]: true }));
  const handleBlur = (name) => setFocused((f) => ({ ...f, [name]: false }));

  const floatLabel = (name) =>
    focused[name] || form[name] ? "active" : "";

  return (
    <>
      <style>{`
        .cf-wrapper {
          background: linear-gradient(135deg, rgba(15,15,15,0.97) 0%, rgba(22,14,5,0.97) 100%);
          border: 1px solid rgba(245,131,39,0.22);
          border-radius: 24px;
          padding: 26px 28px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 32px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset;
        }
        .cf-wrapper::before {
          content: "";
          position: absolute;
          top: -80px;
          right: -80px;
          width: 220px;
          height: 220px;
          background: radial-gradient(circle, rgba(245,131,39,0.18) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }
        .cf-wrapper::after {
          content: "";
          position: absolute;
          bottom: -60px;
          left: -60px;
          width: 180px;
          height: 180px;
          background: radial-gradient(circle, rgba(245,131,39,0.10) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }
        .cf-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(245,131,39,0.12);
          border: 1px solid rgba(245,131,39,0.3);
          border-radius: 999px;
          padding: 4px 14px;
          font-size: 11px;
          font-weight: 600;
          color: #F48428;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 12px;
        }
        .cf-badge span {
          width: 6px; height: 6px;
          background: #F48428;
          border-radius: 50%;
          display: inline-block;
          animation: cf-pulse 1.5s infinite;
        }
        @keyframes cf-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
        .cf-title {
          font-size: 1.55rem;
          font-weight: 800;
          color: #fff;
          margin-bottom: 4px;
          line-height: 1.2;
        }
        .cf-title em {
          font-style: normal;
          color: #F48428;
        }
        .cf-subtitle {
          font-size: 0.82rem;
          color: rgba(255,255,255,0.45);
          margin-bottom: 28px;
        }
        .cf-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(245,131,39,0.25), transparent);
          margin-bottom: 28px;
        }
        .cf-field {
          position: relative;
          margin-bottom: 12px;
        }
        .cf-field label {
          position: absolute;
          top: 50%;
          left: 18px;
          transform: translateY(-50%);
          font-size: 0.82rem;
          color: rgba(255,255,255,0.38);
          pointer-events: none;
          transition: all 0.22s ease;
          background: transparent;
          padding: 0 4px;
          z-index: 1;
        }
        .cf-field.textarea-field label {
          top: 20px;
          transform: none;
        }
        .cf-field label.active {
          top: -1px;
          font-size: 0.7rem;
          color: #F48428;
          background: linear-gradient(to bottom, #0f0f0f 55%, transparent);
        }
        .cf-field.textarea-field label.active {
          top: -10px;
        }
        .cf-field input,
        .cf-field textarea {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 14px;
          padding: 12px 16px 8px;
          color: #fff;
          font-size: 0.88rem;
          outline: none;
          transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
          font-family: inherit;
          resize: none;
        }
        .cf-field input:focus,
        .cf-field textarea:focus {
          border-color: rgba(245,131,39,0.55);
          background: rgba(245,131,39,0.04);
          box-shadow: 0 0 0 3px rgba(245,131,39,0.1);
        }
        .cf-field input::placeholder,
        .cf-field textarea::placeholder {
          color: transparent;
        }
        .cf-field .cf-icon {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255,255,255,0.2);
          font-size: 13px;
          pointer-events: none;
          transition: color 0.25s ease;
        }
        .cf-field.textarea-field .cf-icon {
          top: 20px;
          transform: none;
        }
        .cf-field input:focus ~ .cf-icon,
        .cf-field textarea:focus ~ .cf-icon {
          color: rgba(245,131,39,0.55);
        }
        .cf-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        @media (max-width: 480px) {
          .cf-row { grid-template-columns: 1fr; }
          .cf-wrapper { padding: 28px 20px; }
        }
        .cf-btn {
          width: auto;
          min-width: 200px;
          margin: 4px auto 0;
          background: linear-gradient(135deg, #F48428 0%, #e06810 100%);
          border: none;
          border-radius: 14px;
          padding: 11px 20px;
          color: #fff;
          font-weight: 700;
          font-size: 0.9rem;
          letter-spacing: 0.03em;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 4px;
          position: relative;
          overflow: hidden;
        }
        .cf-btn::before {
          content: "";
          position: absolute;
          top: 0; left: -100%;
          width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          transition: left 0.5s ease;
        }
        .cf-btn:hover::before { left: 100%; }
        .cf-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 30px rgba(245,131,39,0.35);
        }
        .cf-btn:active { transform: translateY(0); }
        .cf-btn:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
        .cf-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: cf-spin 0.7s linear infinite;
          display: inline-block;
        }
        @keyframes cf-spin { to { transform: rotate(360deg); } }
        .cf-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin-top: 12px;
        }
        .cf-trust {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.72rem;
          color: rgba(255,255,255,0.3);
        }
        .cf-trust i { color: rgba(245,131,39,0.6); font-size: 11px; }
        .cf-success {
          text-align: center;
          padding: 40px 20px;
        }
        .cf-success-icon {
          width: 72px; height: 72px;
          background: linear-gradient(135deg, rgba(245,131,39,0.2), rgba(245,131,39,0.05));
          border: 2px solid rgba(245,131,39,0.45);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 28px;
          color: #F48428;
          animation: cf-pop 0.5s ease;
        }
        @keyframes cf-pop {
          0% { transform: scale(0.4); opacity: 0; }
          70% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        .cf-success h4 { color: #fff; font-weight: 700; margin-bottom: 8px; }
        .cf-success p { color: rgba(255,255,255,0.45); font-size: 0.85rem; }
      `}</style>

      <div className="cf-wrapper">
        {submitted ? (
          <div className="cf-success">
            <div className="cf-success-icon">
              <i className="fa-solid fa-check"></i>
            </div>
            <h4>We've got your request!</h4>
            <p>Our experts will reach out within 24 hours. Keep an eye on your inbox.</p>
          </div>
        ) : (
          <>
            <h3 className="cf-title">
              Let's Build <em>Something Great</em>
            </h3>
            <p className="cf-subtitle">
              Fill in your details — we'll respond within 24 hrs.
            </p>
            <div className="cf-divider"></div>

            <form onSubmit={handleSubmit} noValidate>
              {/* Row 1: Name + Email */}
              <div className="cf-row">
                <div className={`cf-field ${floatLabel("fullName") === "active" ? "active" : ""}`}>
                  <label className={floatLabel("fullName")}>Full Name *</label>
                  <input
                    name="fullName"
                    type="text"
                    placeholder="Full Name"
                    required
                    value={form.fullName}
                    onChange={handleChange}
                    onFocus={() => handleFocus("fullName")}
                    onBlur={() => handleBlur("fullName")}
                  />
                  <i className="fa-solid fa-user cf-icon"></i>
                </div>
                <div className="cf-field">
                  <label className={floatLabel("email")}>Email *</label>
                  <input
                    name="email"
                    type="email"
                    placeholder="Email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    onFocus={() => handleFocus("email")}
                    onBlur={() => handleBlur("email")}
                  />
                  <i className="fa-solid fa-envelope cf-icon"></i>
                </div>
              </div>

              {/* Row 2: Phone + Company */}
              <div className="cf-row">
                <div className="cf-field">
                  <label className={floatLabel("phone")}>Phone Number *</label>
                  <input
                    name="phone"
                    type="tel"
                    placeholder="Phone"
                    required
                    value={form.phone}
                    onChange={handleChange}
                    onFocus={() => handleFocus("phone")}
                    onBlur={() => handleBlur("phone")}
                  />
                  <i className="fa-solid fa-phone cf-icon"></i>
                </div>
                <div className="cf-field">
                  <label className={floatLabel("companyName")}>Company Name</label>
                  <input
                    name="companyName"
                    type="text"
                    placeholder="Company"
                    value={form.companyName}
                    onChange={handleChange}
                    onFocus={() => handleFocus("companyName")}
                    onBlur={() => handleBlur("companyName")}
                  />
                  <i className="fa-solid fa-building cf-icon"></i>
                </div>
              </div>

              {/* Row 3: Requirement */}
              <div className="cf-field textarea-field">
                <label className={floatLabel("message")}>
                  Your Requirement *
                </label>
                <textarea
                  name="message"
                  rows={3}
                  placeholder="Describe your requirement"
                  required
                  value={form.message}
                  onChange={handleChange}
                  onFocus={() => handleFocus("message")}
                  onBlur={() => handleBlur("message")}
                />
                <i className="fa-solid fa-pen-to-square cf-icon"></i>
              </div>

              {/* Submit */}
              <button type="submit" className="cf-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="cf-spinner"></span> Sending…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-paper-plane"></i>
                    {buttonText}
                  </>
                )}
              </button>
            </form>

            <div className="cf-footer">
              <div className="cf-trust">
                <i className="fa-solid fa-shield-halved"></i> 100% Private
              </div>
              <div className="cf-trust">
                <i className="fa-solid fa-clock"></i> Reply in 24h
              </div>
              <div className="cf-trust">
                <i className="fa-solid fa-star"></i> No Spam
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
